import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCompanies, getTopRated } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Building2, LayoutGrid, Map as MapIcon } from 'lucide-react';
import CompanyCard from '@/components/CompanyCard';
import FilterSortModal from '@/components/FilterSortModal';
import CompaniesMap from '@/components/CompaniesMap';
import { logger } from '@/lib/logger';

export default function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [topRatedIds, setTopRatedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'map' ? 'map' : 'grid');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    specialization: searchParams.get('specialization') || '',
    minRating: parseFloat(searchParams.get('minRating')) || 0,
    sortBy: 'rating',
    sortOrder: searchParams.get('sortOrder') || 'desc'
  });

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchQuery || undefined,
        category: filters.category || undefined,
        specialization: filters.specialization || undefined,
        min_rating: filters.minRating > 0 ? filters.minRating : undefined,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };
      
      const response = await getCompanies(params);
      setCompanies(response.data);
      
      // Fetch top rated
      try {
        const topRes = await getTopRated();
        setTopRatedIds(new Set(topRes.data.top_ids || []));
      } catch (e) { logger.error('getTopRated failed', e); }
    } catch (error) {
      logger.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [filters]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchCompanies();
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.minRating > 0) params.set('minRating', newFilters.minRating.toString());
    params.set('sortOrder', newFilters.sortOrder);
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      category: '',
      minRating: 0,
      sortBy: 'rating',
      sortOrder: 'desc'
    };
    setFilters(resetFilters);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="directory-page">
      {/* Header */}
      <div className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Find Håndværker
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-8">
            Gennemse vores udvalg af kvalificerede håndværkere og find den perfekte match til dit projekt
          </p>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Søg efter virksomhed eller service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white border-0 text-foreground"
                data-testid="directory-search-input"
              />
            </div>
            <FilterSortModal
              filters={filters}
              onApplyFilters={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Active Filters Display */}
        {(filters.category || filters.minRating > 0) && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.category && (
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                {filters.category}
              </span>
            )}
            {filters.minRating > 0 && (
              <span className="bg-[#f5e6d8] text-[#6b3e25] px-3 py-1 rounded-full text-sm font-medium">
                Min. {filters.minRating} stjerner
              </span>
            )}
          </div>
        )}

        {/* Results Count + View Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <p className="text-muted-foreground">
            {loading ? 'Søger...' : `${companies.length} håndværkere fundet`}
          </p>
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center rounded-full bg-stone-100 p-1" data-testid="directory-view-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  viewMode === 'grid' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="view-grid-btn"
              >
                <LayoutGrid className="w-4 h-4" /> Liste
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  viewMode === 'map' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4" /> Kort
              </button>
            </div>
            {viewMode === 'grid' && (
              <p className="text-sm text-muted-foreground">
                Sorteret efter: Bedømmelse ({filters.sortOrder === 'desc' ? 'Højest' : 'Lavest'} først)
              </p>
            )}
          </div>
        </div>

        {viewMode === 'map' ? (
          <CompaniesMap defaultCategory={filters.category || ''} />
        ) : loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-48 w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companies.map((company) => (
              <CompanyCard key={company.id} company={company} isTopRated={topRatedIds.has(company.id)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Ingen resultater</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Vi kunne ikke finde nogen håndværkere der matcher dine søgekriterier. 
              Prøv at justere dine filtre eller søgeord.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
